import { yupResolver } from "@hookform/resolvers/yup";
import { toUnicodeLocale } from "@utils/string";
import { useLocale, useTranslations } from "next-intl";
import { useRouter, useSearchParams } from "next/navigation";
import React, { FC, useContext, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";

import FilterAction from "@/components/ui/Form/Filters/FilterAction";
import { cleanParams } from "@/components/ui/Form/Filters/FilterUtils";
import { SearchFormSchema, SearchFormSchemaType } from "@/components/ui/Form/Filters/common";
import Autocomplete from "@/components/ui/Form/Filters/components/Autocomplete/Autocomplete";
import Icon from "@/components/ui/Icon/Icon";
import { NavigationContext } from "@/context/NavigationContext";
import { SearchContext } from "@/context/SearchContext";
import { UserContext } from "@/context/UserContext";

export const LOCATION_MOBILE = "location-mobile";
const LocationMobile: FC = () => {
  const query = useSearchParams();
  const locale = useLocale();
  const initialized = useRef<boolean>();
  const t = useTranslations();
  const router = useRouter();
  const { dispatch, counterTotal, counterDispatch, counterParams } = useContext(SearchContext);
  const { setMobilePanelID } = useContext(NavigationContext);
  const { currency, measurement } = useContext(UserContext);
  const { control, watch, handleSubmit, register, reset, setValue } = useForm<SearchFormSchemaType>({
    resolver: yupResolver(SearchFormSchema),
    defaultValues: {
      currency: currency,
      measurement: measurement,
      locale: toUnicodeLocale(locale),
    },
  });

  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    initialized.current = true;
  }, []);
  useEffect(() => {
    if (initialized.current) {
      const { unsubscribe } = watch((value) => {
        const payload = { ...value };
        counterDispatch({ action: "COUNT", payload: { ...payload } });
      });
      return () => unsubscribe();
    }
  }, [watch]);
  const submitHandler = handleSubmit(
    (values) => {
      console.log("User clicked on the Go Button!", values);
      const payload = { ...values };
      dispatch({ action: "SEARCH", payload: { ...payload } });
    },
    (errors) => {
      console.log("errors", errors);
    },
  );

  useEffect(() => {
    if (query.get("category")) {
      setValue("category", query.get("category")?.split(","));
    }
  }, [query]);

  // Handle iOS keyboard events - detect "Go" button press
  useEffect(() => {
    // This specifically helps with iOS keyboard "Go" button
    const handleIosSubmit = (e: Event) => {
      // Check if the event is from an input within our form
      if (e.target && formRef.current && formRef.current.contains(e.target as Node) && (e.target as HTMLElement).tagName === "INPUT") {
        console.log("Inside UseEffect User clicked on the Go Button!");
        // Submit the form
        submitHandler();
      }
    };

    // Add an event listener for the form submission
    document.addEventListener("submit", handleIosSubmit);

    return () => {
      document.removeEventListener("submit", handleIosSubmit);
    };
  }, [submitHandler]);

  const resetHandler = () => {
    reset({});
    const currentRawURL = new URL(window.location.href);
    let currentURL = `${currentRawURL.origin}/${locale}/search/`;
    if (currentRawURL.searchParams.has("type")) {
      currentURL = `${currentURL}?type=${currentRawURL.searchParams.get("type")}`;
    }

    router.push(currentURL);
    counterDispatch({ action: "COUNT", payload: {} });
    dispatch({ action: "REMOVE", payload: { category: [] } });
    dispatch({ action: "SEARCH", payload: currentRawURL.searchParams.has("onlyAvailable") ? { onlyAvailable: true } : {} });
  };

  // Handler for keyboard "Go" button press on iOS
  const handleKeyDown = (e: React.KeyboardEvent) => {
    console.log("User Is typing from keyboard!", e.key);
    if (e.key === "Enter") {
      e.preventDefault();
      // Close keyboard
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
      // Submit the form
      submitHandler();
    }
  };

  return (
    <form ref={formRef} method="GET" action={`/${locale}/search/`} onSubmit={submitHandler} name={LOCATION_MOBILE}>
      <input {...register("currency")} type="hidden" />
      <input {...register("measurement")} type="hidden" />
      <input {...register("locale")} type="hidden" />
      <div className="h-dvh flex flex-col p-m">
        <div className="flex justify-between items-center">
          <h3 className="font-primary text-s uppercase py-m">{t("filter.label.propertyTypeSearch")}</h3>
          <button type="button" onClick={() => setMobilePanelID("")}>
            <Icon name="close" size="20" className="stroke-black" />
          </button>
        </div>
        <div className="flex-grow">
          <div className="border py-s rounded-full">
            <Autocomplete
              fullHeight
              container={LOCATION_MOBILE}
              icon="geotag"
              control={control}
              reverse={false}
              name="category"
              placeholder={t("search.label.locationPlaceholder")}
              onKeyDown={handleKeyDown}
            />
          </div>
        </div>
        <div>
          <FilterAction
            setDisplayFilters={() => setMobilePanelID("")}
            fullWidth
            resetHandler={resetHandler}
            submitHandler={submitHandler}
            submitLabel={t("button.label.showAllUnits", { total: Object.keys(cleanParams(counterParams)).length === 0 ? 0 : counterTotal })}
            filterName="categories"
          />
        </div>
      </div>
    </form>
  );
};
export default LocationMobile;
